<template>
  <div class="pa-4">
    <v-btn
      color="primary"
      :loading="downloading"
      :disabled="downloading"
      @click="handleDownload"
    >
      <v-icon left>mdi-download</v-icon>
      Скачать userIds (csv) с тагом Договор VAT
    </v-btn>

    <div v-if="error" class="error--text mt-2">
      {{ error }}
    </div>

    <v-divider class="my-4" />

    <div class="d-flex align-center" style="gap: 16px">
      <v-select
        v-model="eventType"
        :items="eventOptions"
        label="Event type"
        style="max-width: 240px"
        dense
        outlined
        hide-details
      />
      <DatePicker
        v-model="startDate"
        label="Start date"
        :max-date="startDateMax"
        style="max-width: 180px"
      />
      <DatePicker
        v-model="endDate"
        label="End date"
        :min-date="startDate"
        :max-date="endDateMax"
        style="max-width: 180px"
      />
      <v-btn
        color="primary"
        :loading="downloadingEvents"
        :disabled="downloadingEvents || !eventType || !startDate || !endDate"
        @click="handleEventDownload"
      >
        <v-icon left>mdi-download</v-icon>
        Download events
      </v-btn>
    </div>

    <div v-if="eventError" class="error--text mt-2">
      {{ eventError }}
    </div>

    <v-divider class="my-4" />

    <div class="d-flex align-center" style="gap: 16px">
      <v-select
        v-model="clientVoteType"
        :items="clientVoteTypeOptions"
        label="Vote type"
        style="max-width: 240px"
        dense
        outlined
        hide-details
      />
      <DatePicker
        v-model="clientVoteStartDate"
        label="Start date"
        :max-date="clientVoteStartDateMax"
        style="max-width: 180px"
      />
      <DatePicker
        v-model="clientVoteEndDate"
        label="End date"
        :min-date="clientVoteStartDate"
        :max-date="clientVoteEndDateMax"
        style="max-width: 180px"
      />
      <v-btn
        color="primary"
        :loading="downloadingClientVotes"
        :disabled="
          downloadingClientVotes ||
          !clientVoteType ||
          !clientVoteStartDate ||
          !clientVoteEndDate
        "
        @click="handleClientVoteDownload"
      >
        <v-icon left>mdi-download</v-icon>
        Download client votes
      </v-btn>
    </div>

    <div v-if="clientVoteError" class="error--text mt-2">
      {{ clientVoteError }}
    </div>
  </div>
</template>

<script>
import { downloadVatAgreementUserIds } from '../../../api/admin/chats';
import { downloadEvents } from '../../../api/admin/events';
import { downloadClientVotes } from '../../../api/admin/clientVotes';
import { EVENT } from '@/lib/const';
import { getISODate } from '@/lib/utils';
import DatePicker from '@/components/common/datePicker/DatePicker';

const MAX_RANGE_DAYS = 90;

export default {
  name: 'Downloads',
  components: { DatePicker },
  data() {
    const today = getISODate(new Date());
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);

    return {
      downloading: false,
      error: null,
      eventType: EVENT.CALL_REQUEST,
      startDate: getISODate(lastWeek),
      endDate: today,
      downloadingEvents: false,
      eventError: null,
      clientVoteType: 'serviceRating',
      clientVoteStartDate: getISODate(lastWeek),
      clientVoteEndDate: today,
      downloadingClientVotes: false,
      clientVoteError: null,
    };
  },
  computed: {
    eventOptions() {
      return Object.values(EVENT).map(value => ({
        text: value,
        value,
      }));
    },
    clientVoteTypeOptions() {
      return [
        { text: 'Service Rating', value: 'serviceRating' },
        { text: 'Lead Follow Up', value: 'leadFollowUp' },
      ];
    },
    startDateMax() {
      return this.endDate || getISODate(new Date());
    },
    endDateMax() {
      if (!this.startDate) return getISODate(new Date());

      const maxByRange = new Date(this.startDate);
      maxByRange.setDate(maxByRange.getDate() + MAX_RANGE_DAYS);

      const today = new Date();
      const limit = maxByRange < today ? maxByRange : today;
      return getISODate(limit);
    },
    clientVoteStartDateMax() {
      return this.clientVoteEndDate || getISODate(new Date());
    },
    clientVoteEndDateMax() {
      if (!this.clientVoteStartDate) return getISODate(new Date());

      const maxByRange = new Date(this.clientVoteStartDate);
      maxByRange.setDate(maxByRange.getDate() + MAX_RANGE_DAYS);

      const today = new Date();
      const limit = maxByRange < today ? maxByRange : today;
      return getISODate(limit);
    },
  },
  watch: {
    clientVoteStartDate(newStart) {
      if (!this.clientVoteEndDate || !newStart) return;
      const start = new Date(newStart);
      const end = new Date(this.clientVoteEndDate);
      const diffDays = (end - start) / (1000 * 60 * 60 * 24);
      if (diffDays > MAX_RANGE_DAYS || diffDays < 0) {
        const capped = new Date(newStart);
        capped.setDate(capped.getDate() + MAX_RANGE_DAYS);
        const today = new Date();
        this.clientVoteEndDate = getISODate(capped < today ? capped : today);
      }
    },
    startDate(newStart) {
      if (!this.endDate || !newStart) return;
      const start = new Date(newStart);
      const end = new Date(this.endDate);
      const diffDays = (end - start) / (1000 * 60 * 60 * 24);
      if (diffDays > MAX_RANGE_DAYS || diffDays < 0) {
        const capped = new Date(newStart);
        capped.setDate(capped.getDate() + MAX_RANGE_DAYS);
        const today = new Date();
        this.endDate = getISODate(capped < today ? capped : today);
      }
    },
  },
  methods: {
    async handleDownload() {
      this.downloading = true;
      this.error = null;

      try {
        const response = await downloadVatAgreementUserIds();

        const blob = new Blob([response.data], { type: 'text/csv' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'vat-agreement-users.csv';
        link.click();

        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Download failed:', err);
        this.error = 'Failed to download file. Please try again.';
      } finally {
        this.downloading = false;
      }
    },
    async handleEventDownload() {
      this.downloadingEvents = true;
      this.eventError = null;

      try {
        const response = await downloadEvents({
          type: this.eventType,
          startDate: this.startDate,
          endDate: this.endDate,
        });

        const disposition = response.headers['content-disposition'];
        let filename = `${this.eventType}-events.json`;
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }

        const blob = new Blob([response.data], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();

        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Event download failed:', err);
        this.eventError = 'Failed to download events. Please try again.';
      } finally {
        this.downloadingEvents = false;
      }
    },
    async handleClientVoteDownload() {
      this.downloadingClientVotes = true;
      this.clientVoteError = null;

      try {
        const response = await downloadClientVotes({
          type: this.clientVoteType,
          startDate: this.clientVoteStartDate,
          endDate: this.clientVoteEndDate,
        });

        const disposition = response.headers['content-disposition'];
        let filename = `client-votes-${this.clientVoteType}.csv`;
        if (disposition) {
          const match = disposition.match(/filename="?([^"]+)"?/);
          if (match) filename = match[1];
        }

        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();

        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Client votes download failed:', err);
        this.clientVoteError =
          'Failed to download client votes. Please try again.';
      } finally {
        this.downloadingClientVotes = false;
      }
    },
  },
};
</script>
